/* eslint-disable max-classes-per-file */
import {
  Component,
  Directive,
  Input, OnInit,
} from '@angular/core';
import {
  ValidationErrors,
  FormControl, AbstractControl,
} from '@angular/forms';
import moment from 'moment';
import {BsDatepickerInputDirective, DatepickerDateCustomClasses} from 'ngx-bootstrap/datepicker';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker/bs-datepicker.config';
import { BsDatepickerValue, GenericValidatorFn, injectParentForms } from 'webcommon/shared';
import {ApBaseCVAFormControl, ApChainCVA} from '../base-control';

// Navigate to the file for BsDatepickerValue
// for more notes on this alias
export type ApDatePickerValue = BsDatepickerValue;
@Component({
  selector: 'ap-date-picker',
  styleUrls: [
    './ap-date-picker.component.less',
  ],
  templateUrl: './ap-date-picker.component.html',
})
export class ApDatePickerComponent
extends ApBaseCVAFormControl<FormControl<ApDatePickerValue>>
implements OnInit {
  private parentForms = injectParentForms();
  protected get disabled(): boolean {
    return this.innerControl ? this.innerControl.disabled : false;
  }

  @Input() baseId?: string;
  @Input() labelName: string;
  @Input() showLabel = true;
  // * `BsDatepickerConfig` that can be used to override the default config.
  // * **Note**: This will override any custom date format
  @Input() customBSConfig?: Partial<BsDatepickerConfig>;
  @Input() showRequiredOnly = false;
  @Input() maxDate?: Date;
  @Input() minDate?: Date;

  // Ignore the time component of the date when selecting, which means when selecting dates,
  // you'll always get a value that's at the start of the day.
  // This matters when filtering stuff based on dates on the server.
  // This is opt-in for now, but it feels like it should probably be usable in most cases,
  // since this component doesn't allow the user to pick a time, and the bsDatepicker defaults to the current time.
  @Input() defaultToStartOfDay = false;
  // This is the same as defaultTimeToStart, but for the end of the day,
  // like if you're picking the end of a date range or something
  @Input() defaultToEndOfDay = false;

  // Default config that will be used for the date picker
  bsConfig: Partial<BsDatepickerConfig> = {
    adaptivePosition: true,
    containerClass: 'theme-emds-datepicker',
    dateInputFormat: 'M/D/YYYY',
    selectFromOtherMonth: true,
    showWeekNumbers: false,
  };

  error?: string; // String representation of the last `ValidationErrors`

  id: string;  // Id used to identify the child components
  dateCustomClasses?: DatepickerDateCustomClasses[];

  constructor(
  ) {
    super();
  }

  ngOnInit(): void {
    this.setId();
    if (this.customBSConfig) {
      this.bsConfig = this.customBSConfig;
    }

    const now = new Date();
    this.dateCustomClasses = [
      { date: now, classes: ['emds-datepicker-current-date-background-color'] },
    ];
  }

  isFormSubmitted(): boolean {
    for (const parentForm of this.parentForms) {
      if (parentForm.submitted) {
        return true;
      }
    }

    return false;
  }

  isRequired(): boolean {
    return !this.isShowRequiredOnly() && this.required;
  }

  isShowRequiredOnly(): boolean {
    return this.showRequiredOnly;
  }

  private isDate(value: any): boolean {
    return !isNaN(Date.parse(value));
  }

  // Sets the current `error` as a string representation of the `ValidationErrors`
  private setErrors(error: string): ValidationErrors {
    this.error = error;

    return { error };
  }

  private clearError(): null {
    this.error = undefined;

    return null;
  }

  private setId() {
    this.id = this.baseId + '-date-picker';
  }

  onApplyDatePickerStyle(): any {
    const obj: any = {};
    if (this.required || this.isShowRequiredOnly()) {
      obj.backgroundColor = '#fafad2';
    }
    return obj;
  }

  protected override onOuterControlChanged(outerControl: AbstractControl) {
    super.onOuterControlChanged(outerControl);
    outerControl.addValidators([this.validate]);
    // because the validators changed, we need to update the validity
    outerControl.updateValueAndValidity({emitEvent: false});
  }

  // TODO: move this comment to test cases
  /**
   * ## Validates the form control
   * ## Use cases:
   * ---
   * ### Case 0:
   *    Required: false
   *    Value: '' or null or undefined
   *
   *    Result: null
   * ---
   * ### Case 1:
   *    Required: false
   *    Value: valid date
   *
   *    Result: null
   * ---
   * ### Case 2:
   *    Required: false
   *    Value: invalid date
   *
   *    Result: error like 'date is invalid'
   * ---
   * ### Case 3:
   *    Required: true
   *    Value: '' or null or undefined
   *
   *    Result: error like 'required field'
   * ---
   * ### Case 4:
   *    Required: true
   *    Value: valid date
   *
   *    Result: null
   * ---
   * ### Case 5:
   *    Required: true
   *    Value: invalid date
   *
   *    Result: error like 'date is invalid'
   */
  validate: GenericValidatorFn<ApDatePickerValue> = (control) => {
    if (!control.value || !this.isDate(control.value)) {
      if (this.isShowRequiredOnly()) return this.clearError();
      else {
        if (!control.value) {
          return this.required ? this.setErrors(this.labelName + ' is a required field') : this.clearError();
        } else {
          return this.setErrors('Date is invalid');
        }
      }
    }

    return this.clearError();
  };

  validateCharacter(event: KeyboardEvent): KeyboardEvent | false {
    if (!isNaN(event.key as any)) {
      return event;
    } else if (event.key === '/' || event.key === '.') {
      return event;
    } else {
      return false;
    }
  }
}


// This directive enhances bsDatepicker to allow for defaulting to end of day or start of day
// when selecting a date, but it could grow to include any other value logic
@Directive({
  selector: 'input[bsDatepicker][apBsDatePicker]',
})
export class ApDatePickerInputDirective extends ApChainCVA(BsDatepickerInputDirective)<BsDatepickerValue>() {
  // Ignore the time component of the date when selecting, which means when selecting dates,
  // you'll always get a value that's at the start of the day.
  // This matters when filtering stuff based on dates on the server.
  // This is opt-in for now, but it feels like it should probably be usable in most cases,
  // since this component doesn't allow the user to pick a time, and the bsDatepicker defaults to the current time.
  @Input() defaultToStartOfDay = false;
  // This is the same as defaultTimeToStart, but for the end of the day,
  // like if you're picking the end of a date range or something
  @Input() defaultToEndOfDay = false;

  override registerOnChange(fn: (value: BsDatepickerValue) => void): void {
    super.registerOnChange((value) => {
      if (value && this.defaultToStartOfDay) {
        value = moment(value).startOf('day').toDate();
      } else if (value && this.defaultToEndOfDay) {
        value = moment(value).endOf('day').toDate();
      } else {
        value = moment(value).toDate();
      }
      fn(value);
    });
  }
}
